import axios from 'axios';
import { apiClient } from '@/api/axios';
import { API } from '@/api/endpoints';
import {
  presignedUploadSchema,
  type PresignedUpload,
  type PresignUploadPayload,
  type UploadMimeType,
  type UploadResourceType,
} from '@/types/upload.types';
import { FileUploadError } from '@/utils/errors';

export async function presignUpload(payload: PresignUploadPayload): Promise<PresignedUpload> {
  const res = await apiClient.post(API.uploads.presign, payload);
  return presignedUploadSchema.parse(res.data);
}

interface UploadFileParams {
  file: File;
  /** The file's type as the caller validated it; the storage policy pins it. */
  mimeType: UploadMimeType;
  resourceType: UploadResourceType;
  userId: number;
}

/**
 * Presigns, then posts the file straight to storage. Returns the object key
 * the API expects back. A storage failure throws a FileUploadError.
 */
export async function uploadFile({
  file,
  mimeType,
  resourceType,
  userId,
}: UploadFileParams): Promise<string> {
  const presigned = await presignUpload({ userId, resourceType, mimeType });

  const body = new FormData();
  Object.entries(presigned.fields).forEach(([name, value]) => body.append(name, value));
  // The policy is checked in field order: the file must come last.
  body.append('file', file);

  try {
    // Not apiClient: no base URL, bearer token or response envelope here.
    await axios.post(presigned.url, body);
  } catch (error) {
    throw new FileUploadError(undefined, { cause: error });
  }
  return presigned.key;
}
